import { gql } from '@apollo/client';
import { getCSVUploadStatus } from '../../components-v2/CSVUploader/queries';
import { client } from '../../lib/apollo';
import { HIJACKED_USER_ID_HEADER_NAME } from '../../lib/constants';
import { ProcessUploadMutation, ProcessUploadMutationVariables } from '../types';
import { getUploadURL } from './getUploadUrl';

const PROCESS_UPLOAD = gql`
  mutation ProcessUpload(
    $Bucket: String!
    $Key: String!
    $accountId: Int
    $fileName: String!
    $integrationId: String
    $timezone: String!
    $replacedFileId: Int
    $otherIntegration: Boolean
    $variousIntegrations: Boolean
  ) {
    processUpload(
      Bucket: $Bucket
      Key: $Key
      accountId: $accountId
      fileName: $fileName
      integrationId: $integrationId
      timezone: $timezone
      replacedFileId: $replacedFileId
      otherIntegration: $otherIntegration
      variousIntegrations: $variousIntegrations
    ) {
      requestId
    }
  }
`;

interface UnsupportedKnownCSVErrorProps {
  expectedFile: string;
  message: string;
  exchange: string;
  link?: {
    path: string;
    text: string;
  } | null;
}
// see link below for why Object.setPrototypeOf is necessary
// https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-2.html
export class UnsupportedKnownCSVError extends Error {
  expectedFile: string;
  message: string;
  exchange: string;
  link?: {
    path: string;
    text: string;
  } | null;

  constructor({ expectedFile, message, exchange, link }: UnsupportedKnownCSVErrorProps) {
    super();
    Object.setPrototypeOf(this, new.target.prototype);
    this.expectedFile = expectedFile;
    this.message = message;
    this.exchange = exchange;
    this.link = link;
  }
}

export class UploadNeedsReviewError extends Error {
  constructor() {
    super();
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

interface UploadFileProps {
  file: File;
  integrationId: string | null;
  timezone: string; // ex. 'Asia/Kolkata'
  onProgress: (props: { percent: number }) => void;
  onProcessStart: () => void;
  userId?: number;
  replacedFileId?: number;
  otherIntegration?: boolean;
  variousIntegrations?: boolean;
}

export type UploadedFile = {
  Bucket: string;
  Key: string;
  fileName: string;
};

type UploadFileToS3Props = {
  file: File;
  onProgress?: (props: { percent: number }) => void;
  userId?: number;
};

type ProcessUploadedFileProps = UploadedFile & {
  accountId?: number;
  integrationId?: string | null;
  timezone: string;
  userId?: number;
  replacedFileId?: number;
  otherIntegration?: boolean;
  variousIntegrations?: boolean;
};

const POLL_INTERVAL_MS = 1000;

export const uploadFileToS3 = async ({
  file,
  onProgress,
  userId,
}: UploadFileToS3Props): Promise<UploadedFile> => {
  const fileName = file.name;
  const { Bucket, Key, url } = await getUploadURL({ fileName }, userId);

  if (!url) throw new Error(`Could not upload file "${fileName}"`);

  const fileBuffer = await file.arrayBuffer();

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress?.({ percent });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', 'text/csv');
    xhr.send(fileBuffer);
  });

  return { Bucket, Key, fileName };
};

type WaitForCsvProcessingProps = {
  fileName: string;
  requestId: string;
  userId?: number;
};

// Polls an uploaded CSV's import request until it's processed. Resolves with the import's credential id,
// or throws when the CSV is unsupported, needs review or fails
export const waitForCsvProcessing = async ({ fileName, requestId, userId }: WaitForCsvProcessingProps) => {
  let statusResponse;
  do {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    statusResponse = await getCSVUploadStatus(client, { requestId, userId });
    if (!statusResponse) {
      throw new Error('Could not get status for CSV');
    }
  } while (['created', 'running'].includes(statusResponse.data.csvUploadStatus.status));

  const { credentialId, status, unsupportedKnownCsv } = statusResponse.data.csvUploadStatus;

  switch (status) {
    case 'failed': {
      if (unsupportedKnownCsv) {
        const { expectedFile, message, exchange, link } = unsupportedKnownCsv;
        throw new UnsupportedKnownCSVError({
          expectedFile,
          message,
          exchange,
          link,
        });
      }
      throw new Error(`Failed to process upload for "${fileName}"`);
    }
    case 'inReview':
      throw new UploadNeedsReviewError();
    default:
      return credentialId ?? null;
  }
};

export const processUploadedFile = async ({
  Bucket,
  Key,
  accountId,
  fileName,
  integrationId,
  timezone,
  userId,
  replacedFileId,
  otherIntegration = false,
  variousIntegrations = false,
}: ProcessUploadedFileProps) => {
  const { data: processUploadData } = await client.mutate<
    ProcessUploadMutation,
    ProcessUploadMutationVariables
  >({
    mutation: PROCESS_UPLOAD,
    variables: {
      Bucket,
      Key,
      fileName,
      timezone,
      replacedFileId,
      otherIntegration,
      variousIntegrations,
      ...(accountId !== undefined && { accountId }),
      ...(integrationId !== undefined && { integrationId }),
    },
    ...(userId && { context: { headers: { [HIJACKED_USER_ID_HEADER_NAME]: userId } } }),
  });

  if (!processUploadData) {
    throw new Error(`Could not process upload for "${fileName}"`);
  }

  const { requestId } = processUploadData.processUpload;
  return waitForCsvProcessing({ fileName, requestId, userId });
};

export const uploadFile = async ({
  file,
  integrationId,
  timezone,
  onProgress,
  onProcessStart,
  userId,
  replacedFileId,
  otherIntegration = false,
  variousIntegrations = false,
}: UploadFileProps) => {
  const uploadedFile = await uploadFileToS3({ file, onProgress, userId });

  onProcessStart();

  await processUploadedFile({
    ...uploadedFile,
    integrationId,
    timezone,
    userId,
    replacedFileId,
    otherIntegration,
    variousIntegrations,
  });
};
